'use client';

import { useCallback } from 'react';
import { useImperativeModal, ImperativeModalOptions } from './imperative-modal-context';
import {
  ConfirmationModalAutoClose,
  ConfirmationModalAutoCloseProps,
} from './confirmation-modal-auto-close';

/**
 * Options for configuring a confirmation modal.
 * Extends ConfirmationModalAutoCloseProps but omits the closeModal prop as it's handled internally.
 */
type ConfirmationModalOptions = Omit<ConfirmationModalAutoCloseProps, 'closeModal'> &
  ImperativeModalOptions;

/**
 * Hook for managing confirmation modals - confirmation-style modals with primary and secondary actions.
 *
 * @example
 * ```tsx
 * const { openModal } = useConfirmationModal();
 *
 * // Open a confirmation modal
 * openModal({
 *   title: 'Delete Item',
 *   text: 'Are you sure you want to delete this item?',
 *   primaryButtonText: 'Delete',
 *   onPrimaryButtonClick: async () => {
 *     await deleteItem();
 *   },
 *   secondaryButtonText: 'Cancel'
 * });
 * ```
 * In the example above, the closeModal function is not passed to the ConfirmationModalContent component,
 * because it's handled internally by the useConfirmationModal hook.
 * Additionally the onPrimaryButtonClick is async, so the button will be shown as loading until the promise is resolved. No need to pass that separately. But if you need you can overwrite it via primaryButtonProps.
 * onSecondaryButtonClick is optional, because in most cases secondary button only closes the modal. But if you need something else (like analytics) you can use it.
 *
 * @returns {Object} Modal control functions
 * @returns {Function} openModal - Opens a confirmation modal with the given options
 * @returns {Function} closeModal - Closes the currently open modal
 */
export const useConfirmationModal = () => {
  const { openModal: _openModal, closeModal } = useImperativeModal();

  /**
   * Opens a confirmation modal with the specified configuration.
   *
   * @param {ConfirmationModalOptions} options - Configuration options for the modal
   * @param {string} options.title - Modal title
   * @param {string | ReactNode} [options.text] - Optional modal content text or ReactNode
   * @param {string} options.primaryButtonText - Text for the primary action button
   * @param {Function} options.onPrimaryButtonClick - Async function called when primary button is clicked
   * @param {string} options.secondaryButtonText - Text for the secondary action button
   * @param {Function} [options.onSecondaryButtonClick] - Optional async function called when secondary button is clicked
   * @param {Object} [options.primaryButtonProps] - Optional additional props for primary button
   * @param {Object} [options.secondaryButtonProps] - Optional additional props for secondary button
   * @param {string} [options.width] - Optional modal width (defaults to 'responsive-sm')
   * @param {string} [options.height] - Optional modal height (defaults to 'auto-height')
   * @param {boolean} [options.showCloseButton] - Optional show close button in header (defaults to false)
   * @param {ComponentProps<typeof ConfirmationModalContent>['variant']} [options.variant] - Optional variant for the modal (defaults to 'primary'). 'destructive' is a destructive modal (like delete confirmation). It sets the buttons to destructive variant.
   */
  const openModal = useCallback(
    (options: ConfirmationModalOptions) => {
      const { width, height, showCloseButton, className, classNames, ...confirmationModalProps } =
        options;
      _openModal(
        <ConfirmationModalAutoClose
          {...confirmationModalProps}
          showCloseButton={showCloseButton}
          closeModal={closeModal}
        />,
        {
          width,
          height,
          showCloseButton,
          className,
          classNames,
        },
      );
    },
    [_openModal, closeModal],
  );

  return { openModal, closeModal };
};
